import { campaignData } from './mockData'

export type LeadStatus = 'Converted' | 'In Progress' | 'Dropped'
export type LeadStage =
  | 'Campaign'
  | 'Credit Pull'
  | 'Application'
  | 'Submission'
  | 'Funding'
export type LoanType =
  | 'Purchase'
  | 'Refinance'
  | 'Cash-Out Refinance'
  | 'FHA'
  | 'VA'
  | 'USDA'

export interface Lead {
  id: string
  campaignId: string
  name: string
  email: string
  phone: string
  currentStage: LeadStage
  status: LeadStatus
  loanOfficer: string
  daysInStage: number
  lastActivity: string
  creditPullDate?: string
  applicationDate?: string
  submissionDate?: string
  fundingDate?: string
  loanAmount?: number
  loanType?: LoanType
  currentInterestRate?: string
  previousClosingDate?: string
}

const firstNames = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Barbara',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
]
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
]
const loanOfficers = [
  'Sarah Connor',
  'Kyle Reese',
  'John Matrix',
  'Dutch Schaefer',
]
const loanTypes: LoanType[] = [
  'Purchase',
  'Refinance',
  'Cash-Out Refinance',
  'FHA',
  'VA',
  'USDA',
]

function generateRandomDate(start: Date, end: Date) {
  return new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime()),
  )
    .toISOString()
    .split('T')[0]
}

function generateLeadsForCampaign(campaignId: string, count: number): Lead[] {
  return Array.from({ length: count }).map((_, index) => {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)]
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)]
    const statusRand = Math.random()

    let status: LeadStatus = 'In Progress'
    let stage: LeadStage = 'Campaign'

    if (statusRand > 0.75) {
      status = 'Converted'
      stage = 'Funding'
    } else if (statusRand > 0.6) {
      status = 'Dropped'
      stage = Math.random() > 0.5 ? 'Credit Pull' : 'Application'
    } else {
      const stages: LeadStage[] = [
        'Campaign',
        'Credit Pull',
        'Application',
        'Submission',
      ]
      stage = stages[Math.floor(Math.random() * stages.length)]
    }

    // Generate loan data for leads that have progressed beyond Campaign stage
    const hasLoanData = stage !== 'Campaign'
    const loanAmount = hasLoanData
      ? Math.floor(Math.random() * 500000) + 150000
      : undefined
    const loanType = hasLoanData
      ? loanTypes[Math.floor(Math.random() * loanTypes.length)]
      : undefined
    const currentInterestRate = hasLoanData
      ? (Math.random() * 2 + 5.5).toFixed(3) + '%'
      : undefined

    // Previous closing date only for refinance types
    const isRefinance = loanType?.includes('Refinance')
    const previousClosingDate = isRefinance
      ? generateRandomDate(new Date(2015, 0, 1), new Date(2022, 11, 31))
      : undefined

    return {
      id: `lead-${campaignId}-${index}`,
      campaignId,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      phone: `(555) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
      currentStage: stage,
      status,
      loanOfficer:
        loanOfficers[Math.floor(Math.random() * loanOfficers.length)],
      daysInStage: Math.floor(Math.random() * 30),
      lastActivity: generateRandomDate(new Date(2023, 0, 1), new Date()),
      creditPullDate:
        stage !== 'Campaign'
          ? generateRandomDate(new Date(2023, 0, 1), new Date())
          : undefined,
      applicationDate: ['Application', 'Submission', 'Funding'].includes(stage)
        ? generateRandomDate(new Date(2023, 2, 1), new Date())
        : undefined,
      submissionDate: ['Submission', 'Funding'].includes(stage)
        ? generateRandomDate(new Date(2023, 4, 1), new Date())
        : undefined,
      fundingDate:
        stage === 'Funding'
          ? generateRandomDate(new Date(2023, 6, 1), new Date())
          : undefined,
      loanAmount,
      loanType,
      currentInterestRate,
      previousClosingDate,
    }
  })
}

// Generate leads for each campaign
export const allLeads: Lead[] = campaignData.flatMap(
  (campaign) => generateLeadsForCampaign(campaign.id, 25), // 25 leads per campaign for demo
)
